import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContacts, useCreateContact, useImportCrmContacts, useSyncCrmContacts, useCrmContacts } from '../hooks/useContacts';
import Badge from '../components/ui/Badge';
import Modal from '../components/ui/Modal';
import StatCard from '../components/ui/StatCard';

export default function Contacts() {
  const navigate = useNavigate();
  const { data, isLoading } = useContacts();
  const createContact = useCreateContact();
  const importCrm = useImportCrmContacts();
  const syncCrm = useSyncCrmContacts();
  const contacts = data?.data || [];

  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [selectedCrmIds, setSelectedCrmIds] = useState([]);
  const [form, setForm] = useState({ name: '', company: '', email: '', website: '', tracking_enabled: false });

  const handleCreate = async () => {
    if (!form.name) return;
    try {
      const contact = await createContact.mutateAsync(form);
      setShowCreate(false);
      setForm({ name: '', company: '', email: '', website: '', tracking_enabled: false });
      navigate(`/contacts/${contact.id}`);
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.message || err.message));
    }
  };

  const activeContacts = contacts.filter(c => c.status === 'active');
  const withTracking = contacts.filter(c => c.tracking_enabled);
  const totalAnalyses = contacts.reduce((sum, c) => sum + (c.analyses_count || 0), 0);

  return (
    <div>
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-[23px] font-bold mb-0.5">Kunden</h1>
          <p className="text-gray-500 text-[13.5px]">Kunden die eine CRO-Analyse gebucht haben</p>
        </div>
        <div className="flex gap-2.5">
          <button
            onClick={() => syncCrm.mutate()}
            disabled={syncCrm.isPending}
            className="px-4 py-2.5 border border-gray-200 rounded-lg text-[13px] hover:bg-gray-50 disabled:opacity-50"
          >
            {syncCrm.isPending ? 'Sync läuft…' : 'CRM Sync'}
          </button>
          <button
            onClick={() => setShowImport(true)}
            className="px-4 py-2.5 border border-gray-200 rounded-lg text-[13px] hover:bg-gray-50"
          >
            CRM Import
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2.5 bg-teal text-white rounded-lg text-[13px] font-medium hover:opacity-90"
          >
            + Neuer Kunde
          </button>
        </div>
      </div>

      <div className="flex gap-3.5 mb-5 flex-wrap">
        <StatCard label="Aktive Kunden" value={activeContacts.length || '—'} icon="◉" iconBg="#E6F4F2" iconColor="#0C9488" />
        <StatCard label="Mit Tracking" value={withTracking.length || '—'} icon="◎" iconBg="#DBEAFE" iconColor="#3B82F6" />
        <StatCard label="Analysen gesamt" value={totalAnalyses || '—'} icon="⟳" iconBg="#EDE9FE" iconColor="#8B5CF6" />
        <StatCard label="Dokumente" value={contacts.reduce((s, c) => s + (c.documents_count || 0), 0) || '—'} icon="▤" iconBg="#D1FAE5" iconColor="#10B981" />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-black/[.07]">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 text-left">
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Kunde</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Website</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Tracking</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Analysen</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Dokumente</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium">Status</th>
              <th className="px-5 py-3.5 text-[12px] text-gray-500 font-medium text-right">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <tr
                key={c.id}
                onClick={() => navigate(`/contacts/${c.id}`)}
                className="border-b border-gray-100 last:border-0 hover:bg-gray-50/50 transition-colors cursor-pointer"
              >
                <td className="px-5 py-3.5">
                  <div className="text-[13px] font-medium">{c.company || c.name}</div>
                  {c.company && <div className="text-[11px] text-gray-500">{c.name}</div>}
                </td>
                <td className="px-5 py-3.5 text-[12.5px] text-gray-600 truncate max-w-[200px]">
                  {c.website || '—'}
                </td>
                <td className="px-5 py-3.5">
                  {c.tracking_enabled ? (
                    <Badge bg="#D1FAE5" color="#10B981">Aktiv</Badge>
                  ) : (
                    <Badge bg="#F3F4F6" color="#6B7280">Inaktiv</Badge>
                  )}
                </td>
                <td className="px-5 py-3.5 text-[12.5px] font-medium">{c.analyses_count || 0}</td>
                <td className="px-5 py-3.5 text-[12.5px] font-medium">{c.documents_count || 0}</td>
                <td className="px-5 py-3.5">
                  <Badge
                    bg={c.status === 'active' ? '#D1FAE5' : '#FEF3C7'}
                    color={c.status === 'active' ? '#10B981' : '#F59E0B'}
                  >
                    {c.status === 'active' ? 'Aktiv' : c.status}
                  </Badge>
                </td>
                <td className="px-5 py-3.5 text-right">
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/contacts/${c.id}`); }}
                    className="text-teal text-[13px] font-medium hover:underline"
                  >
                    Details →
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {contacts.length === 0 && !isLoading && (
          <div className="py-12 text-center text-gray-400 text-sm">
            Noch keine Kunden vorhanden. Lege einen neuen Kunden an oder importiere aus dem CRM.
          </div>
        )}
        {isLoading && (
          <div className="py-12 text-center text-gray-400 text-sm">Wird geladen…</div>
        )}
      </div>

      {/* Kunde anlegen Modal */}
      {showCreate && (
        <Modal onClose={() => setShowCreate(false)}>
          <div className="w-[460px]">
            <h2 className="text-[17px] font-bold mb-1">Neuer Kunde</h2>
            <p className="text-[13px] text-gray-500 mb-5">Kunde der eine CRO-Analyse bucht</p>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Name *</label>
                <input
                  className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal"
                  placeholder="Max Mustermann"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Firma</label>
                <input
                  className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal"
                  placeholder="Firma GmbH"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">E-Mail</label>
                <input
                  className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal"
                  placeholder="email@firma.de"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Website</label>
                <input
                  className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal"
                  placeholder="https://firma.de"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                />
              </div>
            </div>

            <div className="flex items-center gap-3 p-3.5 bg-bg rounded-lg mb-5">
              <button
                onClick={() => setForm({ ...form, tracking_enabled: !form.tracking_enabled })}
                className={`w-10 h-[22px] rounded-full transition-colors relative shrink-0 ${form.tracking_enabled ? 'bg-teal' : 'bg-gray-300'}`}
              >
                <div className={`w-[18px] h-[18px] bg-white rounded-full absolute top-[2px] transition-all ${form.tracking_enabled ? 'left-[20px]' : 'left-[2px]'}`} />
              </button>
              <div>
                <div className="text-[13px] font-medium">Tracking aktivieren</div>
                <div className="text-[11.5px] text-gray-500">Tracking-Snippet für die Website des Kunden bereitstellen</div>
              </div>
            </div>

            <div className="flex gap-2.5 justify-end">
              <button
                onClick={() => setShowCreate(false)}
                className="border border-gray-200 rounded-lg px-4 py-2 text-[13px] hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleCreate}
                disabled={!form.name || createContact.isPending}
                className="bg-teal text-white rounded-lg px-4 py-2 text-[13px] font-medium hover:opacity-90 disabled:opacity-50"
              >
                {createContact.isPending ? 'Wird erstellt…' : 'Kunde anlegen'}
              </button>
            </div>
          </div>
        </Modal>
      )}
      {/* CRM Import Modal */}
      {showImport && <CrmImportModal
        existingCrmIds={contacts.filter(c => c.crm_contact_id).map(c => c.crm_contact_id)}
        onImport={async (ids) => {
          await importCrm.mutateAsync({ crm_contact_ids: ids });
          setShowImport(false);
        }}
        isPending={importCrm.isPending}
        onClose={() => setShowImport(false)}
      />}
    </div>
  );
}

function CrmImportModal({ existingCrmIds, onImport, isPending, onClose }) {
  const { data: crmContacts, isLoading } = useCrmContacts();
  const [selected, setSelected] = useState([]);

  const contacts = (crmContacts?.data || crmContacts || []).filter(Boolean);
  const available = contacts.filter(c => !existingCrmIds.includes(String(c.id)));

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAll = () => {
    setSelected(selected.length === available.length ? [] : available.map(c => String(c.id)));
  };

  return (
    <Modal onClose={onClose}>
      <div className="w-[520px]">
        <h2 className="text-[17px] font-bold mb-1">Kunden aus CRM importieren</h2>
        <p className="text-[13px] text-gray-500 mb-4">Wähle Kunden aus backoffice.konversion.digital</p>

        {isLoading ? (
          <div className="py-8 text-center text-gray-400 text-sm">CRM-Kunden werden geladen…</div>
        ) : available.length === 0 ? (
          <div className="py-8 text-center text-gray-400 text-sm">
            {contacts.length === 0 ? 'Keine Kunden im CRM gefunden.' : 'Alle CRM-Kunden wurden bereits importiert.'}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <button onClick={selectAll} className="text-[12px] text-teal font-medium">
                {selected.length === available.length ? 'Alle abwählen' : 'Alle auswählen'}
              </button>
              <span className="text-[12px] text-gray-500">{selected.length} von {available.length} ausgewählt</span>
            </div>
            <div className="max-h-[300px] overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {available.map((c) => (
                <label
                  key={c.id}
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(String(c.id))}
                    onChange={() => toggleSelect(String(c.id))}
                    className="w-4 h-4 rounded border-gray-300 text-teal accent-teal"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium">{c.name}</div>
                    <div className="text-[11.5px] text-gray-500">
                      {[c.company, c.email].filter(Boolean).join(' · ') || '—'}
                    </div>
                  </div>
                  {c.website && <span className="text-[11px] text-gray-400 truncate max-w-[150px]">{c.website}</span>}
                </label>
              ))}
            </div>
          </>
        )}

        <div className="flex gap-2.5 justify-end mt-4">
          <button onClick={onClose} className="border border-gray-200 rounded-lg px-4 py-2 text-[13px] hover:bg-gray-50">
            Abbrechen
          </button>
          <button
            onClick={() => onImport(selected)}
            disabled={selected.length === 0 || isPending}
            className="bg-teal text-white rounded-lg px-4 py-2 text-[13px] font-medium hover:opacity-90 disabled:opacity-50"
          >
            {isPending ? 'Importiere…' : `${selected.length} Kunden importieren`}
          </button>
        </div>
      </div>
    </Modal>
  );
}
